import { Answer as StorageAnswer, HistoryStack as StorageHistoryStack, Tile as StorageTile } from '@/lib/storage'
import { getHistoryStackByTileId, saveHistoryStack, saveTile } from '@/lib/storage'

export interface HistoryStackState {
  past: StorageAnswer[]
  future: StorageAnswer[]
  maxSize: number
}

export class HistoryStackManager {
  private sessionId: string
  private tileId: string
  private stack: HistoryStackState
  private listeners: Set<() => void> = new Set()

  constructor(sessionId: string, tileId: string, maxSize = 50) {
    this.sessionId = sessionId
    this.tileId = tileId
    this.stack = { past: [], future: [], maxSize }
  }

  static async create(sessionId: string, tileId: string, maxSize = 50): Promise<HistoryStackManager> {
    const manager = new HistoryStackManager(sessionId, tileId, maxSize)
    await manager.load()
    return manager
  }

  private async load(): Promise<void> {
    const stored = await getHistoryStackByTileId(this.tileId)
    if (stored) {
      this.stack = {
        past: stored.answers.slice(0, stored.currentIndex + 1),
        future: stored.answers.slice(stored.currentIndex + 1),
        maxSize: stored.maxSize,
      }
    }
  }

  private async persist(): Promise<void> {
    const allAnswers = [...this.stack.past, ...this.stack.future]
    const historyStack: StorageHistoryStack = {
      id: `history-${this.tileId}`,
      sessionId: this.sessionId,
      tileId: this.tileId,
      answers: allAnswers,
      currentIndex: this.stack.past.length - 1,
      maxSize: this.stack.maxSize,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }
    await saveHistoryStack(historyStack)
    this.notify()
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener())
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  getState(): HistoryStackState {
    return { ...this.stack }
  }

  canUndo(): boolean {
    return this.stack.past.length > 0
  }

  canRedo(): boolean {
    return this.stack.future.length > 0
  }

  push(answer: StorageAnswer): void {
    this.stack.past.push(answer)
    this.stack.future = []

    if (this.stack.past.length > this.stack.maxSize) {
      this.stack.past.shift()
    }

    this.persist()
  }

  undo(): StorageAnswer | null {
    if (!this.canUndo()) return null

    const answer = this.stack.past.pop()!
    this.stack.future.unshift(answer)
    this.persist()
    return answer
  }

  redo(): StorageAnswer | null {
    if (!this.canRedo()) return null

    const answer = this.stack.future.shift()!
    this.stack.past.push(answer)
    this.persist()
    return answer
  }

  getCurrentAnswer(): StorageAnswer | null {
    return this.stack.past[this.stack.past.length - 1] ?? null
  }

  setMaxSize(maxSize: number): void {
    this.stack.maxSize = maxSize
    if (this.stack.past.length > maxSize) {
      this.stack.past = this.stack.past.slice(-maxSize)
    }
    this.persist()
  }

  clear(): void {
    this.stack.past = []
    this.stack.future = []
    this.persist()
  }

  markDependentsRecalculating(tileIndex: number, tiles: StorageTile[]): void {
    const dependentTiles = tiles.filter(t => t.index > tileIndex)
    dependentTiles.forEach(async tile => {
      if (!tile.isRecalculating) {
        await saveTile({ ...tile, isRecalculating: true, status: 'recalculating' as StorageTile['status'] })
      }
    })
  }
}

export function createHistoryStackManager(sessionId: string, tileId: string, maxSize = 50): HistoryStackManager {
  return new HistoryStackManager(sessionId, tileId, maxSize)
}